/**
 * Module dependencies.
 */

import * as React from 'react';
import isPromise from 'is-promise';
import { Loader2 } from 'lucide-react';
import { Button as UiButton, ButtonProps as UiButtonProps } from 'app/components/ui/button';
import { cn } from 'app/lib/utils';

/**
 * antd-flavoured prop aliases still used across the app.
 */

type AntdType = 'primary' | 'default' | 'dashed' | 'link' | 'text' | 'ghost';
type AntdSize = 'small' | 'middle' | 'large';

/**
 * `Props` type.
 */

type Props = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'type'> & {
  type?: AntdType;
  danger?: boolean;
  size?: AntdSize;
  icon?: React.ReactNode;
  href?: string;
  target?: string;
  block?: boolean;
  loading?: boolean;
  isLoading?: boolean;
  setLoading?: (value: boolean) => void;
  htmlType?: 'button' | 'submit' | 'reset';
  onClick?: (event?: React.MouseEvent<HTMLElement>) => any;
};

/**
 * Map the antd `type` onto a shadcn variant (danger handled separately).
 */

function toVariant(type?: AntdType): UiButtonProps['variant'] {
  switch (type) {
    case 'primary':
      return 'default';
    case 'text':
    case 'ghost':
      return 'ghost';
    case 'link':
      return 'link';
    default:
      return 'outline';
  }
}

const sizeMap: Record<AntdSize, UiButtonProps['size']> = {
  small: 'sm',
  middle: 'default',
  large: 'lg'
};

/**
 * Export `Button` component.
 *
 * Thin translation layer over the shadcn Button: keeps the antd-ish API that
 * callers (and antd's Popconfirm/Form) still use, and preserves the
 * async-onClick auto-loading behaviour.
 */

export const Button = React.forwardRef<HTMLButtonElement, Props>(function Button(props, ref) {
  const [internalLoading, setInternalLoading] = React.useState(false);
  const {
    className,
    type,
    danger,
    size = 'middle',
    icon,
    href,
    target,
    block,
    loading,
    isLoading = internalLoading,
    setLoading = setInternalLoading,
    htmlType,
    disabled,
    onClick,
    children,
    ...rest
  } = props;

  const busy = !!loading || !!isLoading;

  const handleClick = React.useCallback(
    async (event: React.MouseEvent<HTMLElement>) => {
      if (!onClick) {
        return;
      }

      try {
        const result = onClick(event);

        if (isPromise(result)) {
          setLoading(true);
          setInternalLoading(true);

          await (result as unknown as Promise<unknown>);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
        setInternalLoading(false);
      }
    },
    [onClick, setLoading]
  );

  const isIconOnly = !!icon && (children == null || children === '');
  // `primary + danger` is a solid red button; a bare `danger` (e.g. table row
  // delete) is a lighter red-outline, matching antd's previous treatment.
  const solidDanger = danger && type === 'primary';
  const dangerOutline = danger && type !== 'primary';
  const variant: UiButtonProps['variant'] = solidDanger ? 'destructive' : toVariant(type);
  const size2: UiButtonProps['size'] = isIconOnly ? 'icon' : sizeMap[size] ?? 'default';
  const classes = cn(
    block && 'w-full',
    isIconOnly && size === 'small' && 'h-8 w-8',
    // The shadcn outline/ghost variants inherit their text colour; pin it to
    // foreground so antd icons don't fall back to antd's link blue.
    (variant === 'outline' || variant === 'ghost') && !dangerOutline && 'text-foreground',
    dangerOutline &&
      'border-destructive/30 text-destructive hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive',
    className
  );

  const content = (
    <>
      {busy ? <Loader2 className={'h-4 w-4 animate-spin'} /> : icon}

      {children}
    </>
  );

  if (href) {
    return (
      <UiButton asChild className={classes} size={size2} variant={variant} {...rest}>
        <a href={href} onClick={handleClick as any} target={target}>
          {content}
        </a>
      </UiButton>
    );
  }

  return (
    <UiButton
      className={classes}
      disabled={busy || disabled}
      onClick={handleClick}
      ref={ref}
      size={size2}
      type={htmlType ?? 'button'}
      variant={variant}
      {...rest}
    >
      {content}
    </UiButton>
  );
});
